import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function HelpCenterPage() {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const categories = [
    { id: 'getting-started', icon: 'ri-rocket-line', label: t('helpCenter.gettingStarted') },
    { id: 'membership', icon: 'ri-user-add-line', label: t('helpCenter.membership') },
    { id: 'billing', icon: 'ri-money-dollar-circle-line', label: t('helpCenter.billing') },
    { id: 'technical', icon: 'ri-settings-line', label: t('helpCenter.technical') },
    { id: 'mobile-app', icon: 'ri-smartphone-line', label: t('helpCenter.mobileApp') },
    { id: 'reports', icon: 'ri-bar-chart-line', label: t('helpCenter.reports') }
  ];

  const faqs = [
    {
      category: 'getting-started',
      question: t('helpCenter.faq1.question'),
      answer: t('helpCenter.faq1.answer')
    },
    {
      category: 'getting-started',
      question: t('helpCenter.faq2.question'),
      answer: t('helpCenter.faq2.answer')
    },
    {
      category: 'membership',
      question: t('helpCenter.faq3.question'),
      answer: t('helpCenter.faq3.answer')
    },
    {
      category: 'membership',
      question: t('helpCenter.faq4.question'),
      answer: t('helpCenter.faq4.answer')
    },
    {
      category: 'billing',
      question: t('helpCenter.faq5.question'),
      answer: t('helpCenter.faq5.answer')
    },
    {
      category: 'billing',
      question: t('helpCenter.faq6.question'),
      answer: t('helpCenter.faq6.answer')
    },
    {
      category: 'technical',
      question: t('helpCenter.faq7.question'),
      answer: t('helpCenter.faq7.answer')
    },
    {
      category: 'technical',
      question: t('helpCenter.faq8.question'),
      answer: t('helpCenter.faq8.answer')
    },
    {
      category: 'mobile-app',
      question: t('helpCenter.faq9.question'),
      answer: t('helpCenter.faq9.answer')
    },
    {
      category: 'mobile-app',
      question: t('helpCenter.faq10.question'),
      answer: t('helpCenter.faq10.answer')
    },
    {
      category: 'reports',
      question: t('helpCenter.faq11.question'),
      answer: t('helpCenter.faq11.answer')
    },
    {
      category: 'reports',
      question: t('helpCenter.faq12.question'),
      answer: t('helpCenter.faq12.answer')
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.category === activeCategory &&
    (searchQuery === '' || 
     faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
     faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <a href="/" className="flex items-center cursor-pointer">
              <img src="https://5vix.com/logo.png" alt="5vix Logo" className="h-10 w-10" />
            </a>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <a href="/#home" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.home')}</a>
            <a href="/#services" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.services')}</a>
            <a href="/#register" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.register')}</a>
            <a href="/#blog" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.blog')}</a>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer font-medium"
            >
              {i18n.language === 'en' ? 'ع' : 'EN'}
            </button>
            <a href="/#register" className="bg-[#673ab7] text-white px-6 py-2 rounded-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
              {t('nav.tryFree')}
            </a>
            <button onClick={toggleMobileMenu} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMobileMenu}></div>
          <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-0 w-64 h-full bg-white shadow-lg p-6`}>
            <button onClick={toggleMobileMenu} className="mb-6 text-gray-700 font-medium cursor-pointer">Close</button>
            <nav className="flex flex-col space-y-4">
              <a href="/#home" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.home')}</a>
              <a href="/#services" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.services')}</a>
              <a href="/#register" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.register')}</a>
              <a href="/#blog" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.blog')}</a>
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#673ab7]/10 to-[#673ab7]/5 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {t('helpCenter.title')}
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              {t('helpCenter.subtitle')}
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pl-4 rtl:pl-0 rtl:pr-4 pointer-events-none">
                <i className="ri-search-line text-gray-400 text-xl"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('helpCenter.searchPlaceholder')}
                className="w-full pl-12 rtl:pl-4 rtl:pr-12 pr-4 py-4 text-base bg-white rounded-lg shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#673ab7] focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`p-6 rounded-lg border-2 transition-all cursor-pointer ${
                  activeCategory === category.id
                    ? 'border-[#673ab7] bg-[#673ab7]/5'
                    : 'border-gray-200 hover:border-[#673ab7]/50'
                }`}
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  activeCategory === category.id ? 'bg-[#673ab7] text-white' : 'bg-gray-100 text-[#673ab7]'
                }`}>
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <p className={`text-sm font-medium text-center ${
                  activeCategory === category.id ? 'text-[#673ab7]' : 'text-gray-700'
                }`}>
                  {category.label}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('helpCenter.frequentlyAsked')}
            </h2>

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-gray-400 text-2xl"></i>
                </div>
                <p className="text-gray-600">{t('helpCenter.noResults')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 pr-4 rtl:pr-0 rtl:pl-4">
                        {faq.question}
                      </span>
                      <div className="w-6 h-6 flex items-center justify-center text-[#673ab7]">
                        <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-xl`}></i>
                      </div>
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4 text-gray-700 border-t border-gray-100 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#673ab7] to-[#5e35b1] rounded-2xl p-8 md:p-12 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-3xl"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4">
                {t('helpCenter.stillNeedHelp')}
              </h2>
              <p className="text-white/90 mb-8 text-lg">
                {t('helpCenter.contactTeam')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:info@5vix.com" className="bg-white text-[#673ab7] px-8 py-3 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  {t('helpCenter.emailSupport')}
                </a>
                <a href="tel:201044553254" className="bg-white/10 text-white border-2 border-white px-8 py-3 rounded-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  {t('helpCenter.callUs')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <a href="/" className="text-3xl font-bold text-white mb-4 block cursor-pointer">5vix</a>
              <p className="text-gray-400 mb-6">
                {t('footer.description')}
              </p>
              <div className="flex flex-col space-y-3 mb-6">
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  <a href="mailto:info@5vix.com" className="hover:text-white transition-colors">info@5vix.com</a>
                </div>
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  <a href="tel:201044553254" className="hover:text-white transition-colors">01044553254</a>
                </div>
              </div>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="https://www.tiktok.com/@5vixofficial" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-500 transition-colors cursor-pointer">
                  <i className="ri-tiktok-fill text-white text-xl"></i>
                </a>
                <a href="https://www.youtube.com/@5vix-c3p" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-red-600 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-white text-xl"></i>
                </a>
                <a href="https://www.instagram.com/5vixofficial/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white text-xl"></i>
                </a>
                <a href="https://www.linkedin.com/in/5vix-software-12058b363/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3">
                <li><a href="/#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.home')}</a></li>
                <li><a href="/#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.services')}</a></li>
                <li><a href="/#register" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.register')}</a></li>
                <li><a href="/#blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.blog')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.support')}</h3>
              <ul className="space-y-3">
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.helpCenter')}</a></li>
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.faq')}</a></li>
                <li><a href="tel:201044553254" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.contactUs')}</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.newsletter')}</h3>
              <p className="text-gray-400 mb-4">{t('footer.newsletterDesc')}</p>
              <form className="mb-4">
                <div className="flex">
                  <input
                    type="email"
                    placeholder={t('register.email')}
                    className="bg-gray-800 text-white px-4 py-2 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-[#673ab7] flex-1"
                  />
                  <button className="bg-[#673ab7] text-white px-4 py-2 rounded-r-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
                    {t('footer.subscribe')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 mb-4 md:mb-0">{t('footer.copyright')}</p>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.cookies')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
